import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Reiterleiste, type ReiterEintrag, Card, Badge, Button, Select, Input, useEinsatz, useEinsatzWechsel, useBestaetigung, useConnectionRetry, RequireEinsatz, FensterKnopf } from '@pilot-hub/ui'
import { api, userApi, vehicleApi } from '@pilot-hub/api-client'
import { FUNKTIONEN, funktionenFarbe } from '@pilot-hub/config'
import { useSearchParams } from 'react-router-dom'
import { holeKmData } from '../utils/kraefteManagement'
import {
  BadgeCheck, Car, Radio, Send, X, Plus, Truck, Users, Clock, GripVertical, ListChecks, UserMinus, Star, ScrollText, ChevronDown, ChevronRight
} from 'lucide-react'

/* ------------------------------------------------------------------ Typen */

interface Lizenz { id: number; typ: string | null; abgelaufen?: boolean; ablaufdatum?: string | null }
interface Kraft {
  id: number
  vorname?: string | null
  nachname?: string | null
  benutzername: string
  funktionen: string[]
  lizenzen: Lizenz[]
}
interface Fahrzeug {
  id: number
  rufzeichen?: string | null
  fahrzeugtyp?: string | null
  max_einsatzkraefte?: number | null
  kennzeichen?: string | null
  typ?: string | null
}
interface CrewRow { nutzerId: number | null; funktion: string; beginn: string; ende: string }
interface Block { key: string; fahrzeugId: number | null; besatzung: CrewRow[] }
interface ReserveEintrag { nutzerId: number; verfuegbarAb: string; beginn?: string; ende?: string }
interface EinsatzOption { id: number; titel: string; status: string; typ?: string | null; beginn_geplant?: string | null }

interface KMData {
  bloecke?: Block[]
  reserveWache?: number[]
  einsatzReserve?: ReserveEintrag[]
  einsatzEnde?: ReserveEintrag[]
  /** Beginn/Ende je Nutzer – wird in "Einsatz bearbeiten" gepflegt und muss erhalten bleiben. */
  zeiten?: Record<string, { beginn: string; ende: string }>
  /** Zuletzt bekannte Einsatzfunktion je Nutzer (für die Rückkehr aus Reserve/Einsatzende ins Fahrzeug) */
  funktionMerker?: Record<string, string>
  /** Trupps (nur aktuelle) und Änderungsprotokoll (nur der Server schreibt es) – Format siehe services/*-api/trupps.py */
  trupps?: Trupp[]
  truppProtokoll?: TruppProtokoll[]
}

/* ------------------------------------------------------------ Trupps */

interface TruppHerkunft { nutzerId: number; blockKey: string; fahrzeugId: number | null; funktion: string }
/** reserve gesetzt = Trupp ist in Reserve Wache (Mitglieder stehen in reserveWache); herkunft = bisheriger Fahrzeugblock/Funktion */
interface Trupp { key: string; name: string; typ: string; fuehrerId: number | null; mitglieder: number[]; reserve?: { seit: string; herkunft: TruppHerkunft[] } }
interface TruppProtokoll {
  zeit: string
  aktion: 'GEBILDET' | 'AUFGELOEST' | 'HINZU' | 'ENTFERNT' | 'FUEHRER' | 'UMBENANNT' | 'TYP' | 'RESERVE' | 'EINSATZ'
  truppKey: string
  truppName: string
  nutzerId?: number
  alt?: string | number | null
  neu?: string | number | null
}

const TRUPP_MAX = 3
/** Trupptypen = taktische Zeichen der Gruppe „Einheiten“ (taktischeSymbole.ts); gleiche Liste wie in trupps.py */
const TRUPP_TYPEN: { value: string; label: string }[] = [
  { value: 'drohnentrupp', label: 'Drohnentrupp' },
  { value: 'rettungshund', label: 'Rettungshundetrupp' },
  { value: 'sanitaetsgruppe', label: 'Sanitätstrupp' },
  { value: 'aerztliche_versorgung', label: 'Arzttrupp' },
  { value: 'rettungsdienst', label: 'Rettungsdienst' },
  { value: 'formation_einsatz', label: 'Einsatzeinheit' },
  { value: 'betreuung', label: 'Betreuung' },
  { value: 'psnv', label: 'Psychosoziale Notfallversorgung' },
  { value: 'verpflegung', label: 'Verpflegung' },
  { value: 'technik_sicherheit', label: 'Technik und Sicherheit' },
  { value: 'brandschutz', label: 'Brandschutz / Feuerwehr' },
]
const truppTypLabel = (typ: string) => TRUPP_TYPEN.find(t => t.value === typ)?.label ?? typ

/* ------------------------------------------------------- Lizenz-Icons */

/**
 * Icon je Lizenzart. Reihenfolge ist wichtig:
 * zuerst Funklizenzen (BOS/BZF), dann EASA/Drohnen, dann Führerscheine.
 */
const LIZENZ_ICONS: Array<{ test: RegExp; Icon: typeof BadgeCheck; farbe: string }> = [
  // Droniq BOS-Wissen für Fernpiloten → Drohnen-Icon (vor den Funklizenzen, sonst greift „BOS“)
  { test: /droniq/i,                                          Icon: Send,  farbe: 'text-blue-400' },
  // Funklizenzen (Sprechfunkzeugnis BOS / BZF) → Funkgerät
  { test: /bzf|\bbos\b|sprechfunk|funkzeugnis|funk/i,        Icon: Radio, farbe: 'text-violet-400' },
  // EASA-Zertifikate / Drohnen → Drohnen-Icon
  { test: /easa|fernpilot|a1|a2|a3|\bsts\b|drohne|uas/i,      Icon: Send,  farbe: 'text-blue-400' },
  // Führerscheinklassen + Feuerwehrführerschein → Fahrzeug-Icon
  { test: /führerschein|fuehrerschein|fahrerlaubnis|klasse\s*(b|c1)\b/i, Icon: Car, farbe: 'text-emerald-400' },
]

function LizenzIcon({ typ }: { typ: string | null | undefined }) {
  const t = typ ?? ''
  const treffer = LIZENZ_ICONS.find(l => l.test.test(t))
  const Icon = treffer?.Icon ?? BadgeCheck
  const farbe = treffer?.farbe ?? 'text-emerald-400'
  return (
    <span title={t} className={`inline-flex items-center justify-center ${farbe}`}>
      <Icon className="h-4 w-4" />
    </span>
  )
}

/** Lizenzstatus wie im Admin-Modul (Lizenzen): abgelaufen, läuft ab (innerhalb von 6 Monaten), gültig. */
function lizenzStatus(l: Lizenz): 'abgelaufen' | 'laeuft_ab' | 'gueltig' {
  if (l.abgelaufen) return 'abgelaufen'
  if (!l.ablaufdatum) return 'gueltig'
  const d = new Date(l.ablaufdatum)
  const heute = new Date()
  if (d < heute) return 'abgelaufen'
  const in6Mon = new Date(heute.getFullYear(), heute.getMonth() + 6, heute.getDate())
  return d <= in6Mon ? 'laeuft_ab' : 'gueltig'
}
const LIZENZ_TEXTFARBE = { abgelaufen: 'text-red-400 font-semibold', laeuft_ab: 'text-yellow-400', gueltig: 'text-emerald-400' } as const
const LIZENZ_STATUSTEXT = { abgelaufen: 'abgelaufen', laeuft_ab: 'läuft bald ab', gueltig: 'gültig' } as const

function LizenzNamen({ liste }: { liste: Lizenz[] }) {
  return (
    <span className="text-xs truncate">
      {liste.map((d, i) => {
        const st = lizenzStatus(d)
        return (
          <span key={d.id} data-lizenz-status={st} className={LIZENZ_TEXTFARBE[st]}
            title={`${d.typ ?? ''} – ${LIZENZ_STATUSTEXT[st]}${d.ablaufdatum ? ` (bis ${new Date(d.ablaufdatum).toLocaleDateString('de-DE')})` : ''}`}>
            {i > 0 && <span className="text-dark-text-subtle"> // </span>}{kurzName(d.typ ?? '')}
          </span>
        )
      })}
    </span>
  )
}

const kurzName = (t: string) => {
  if (/droniq/i.test(t)) return 'Droniq'
  if (/a1|a3/i.test(t)) return 'A1/A3'
  if (/a2/i.test(t)) return 'A2'
  if (/bzf/i.test(t)) return 'BZF'
  if (/\bbos\b/i.test(t)) return 'BOS'
  if (/führerschein|fuehrerschein/i.test(t)) {
    if (/groß|gross/i.test(t)) return 'Feuerw. groß'
    if (/klein/i.test(t)) return 'Feuerw. klein'
    const m = /klasse\s*(b|c1)/i.exec(t)
    return m ? `Klasse ${m[1].toUpperCase()}` : 'Führerschein'
  }
  if (/fernpilot/i.test(t)) return 'Fernpilot'
  return t.split(/[ (/-]/)[0].slice(0, 10)
}

/** Als „laufend" gilt alles außer geplant/abgeschlossen. */
const LAUFENDE_STATI = ['LAUFEND', 'ALARMIERUNG', 'ABFAHRT_WACHE', 'ANKUNFT_EINSATZORT', 'EINSATZENDE']


/* ------------------------------------------------------------ Karte */

function KraftKarte({
  kraft, klein, onDragStart, onEntfernen, platzhalterText, ueberzogen, funktion, truppBadge
}: {
  kraft: Kraft
  klein?: boolean
  onDragStart: (e: React.PointerEvent, nutzerId: number) => void
  onEntfernen?: () => void
  platzhalterText?: string
  ueberzogen?: boolean
  funktion?: string
  /** Truppzugehörigkeit, z. B. „Drohnentrupp 1 · TF“ */
  truppBadge?: string
}) {
  const name = `${kraft.vorname ?? ''} ${kraft.nachname ?? ''}`.trim() || kraft.benutzername
  const farbe = funktion ? funktionenFarbe(funktion) : (kraft.funktionen?.length ? funktionenFarbe(kraft.funktionen[0]) : '#111827')
  return (
    <div
      onPointerDown={e => onDragStart(e, kraft.id)}
      style={{ touchAction: 'none', borderColor: ueberzogen ? '#22c55e' : farbe }}
      className={`group flex items-center gap-2 rounded-xl border-2 px-2.5 py-2 select-none cursor-grab active:cursor-grabbing transition-colors
        ${ueberzogen
          ? 'bg-primary-600/20'
          : klein
            ? 'bg-dark-bg-tertiary'
            : 'bg-dark-bg-tertiary hover:border-primary-600/50'}
      `}
    >
      <GripVertical className="h-4 w-4 shrink-0 text-dark-text-subtle" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-dark-text">{name}</div>
        {truppBadge && (
          <span data-trupp-badge className="mt-0.5 inline-flex max-w-full items-center gap-1 rounded-md bg-primary-600/20 px-1.5 py-0.5 text-[11px] font-medium text-primary-200">
            <Users className="h-3 w-3 shrink-0" /><span className="truncate">{truppBadge}</span>
          </span>
        )}
        <div className="mt-0.5 flex flex-col gap-0.5 min-w-0">
          {kraft.lizenzen.length === 0
            ? <span className="text-xs text-dark-text-subtle">keine Lizenz</span>
            : (() => {
                const drohnen = kraft.lizenzen.filter(l =>
                  /droniq|fernpilot|a1|a2|a3|sts|drohne|uas|easa/i.test(l.typ ?? ''))
                const fuehrerscheine = kraft.lizenzen.filter(l =>
                  /führerschein|fuehrerschein|klasse\s*[bc1]/i.test(l.typ ?? ''))
                const funklizenzen = kraft.lizenzen.filter(l =>
                  !/droniq/i.test(l.typ ?? '') && /bzf|\bbos\b|sprechfunk|funkzeugnis|funk/i.test(l.typ ?? ''))
                const sonstige = kraft.lizenzen.filter(l =>
                  !/fernpilot|a1|a2|a3|sts|drohne|uas|easa|führerschein|fuehrerschein|klasse\s*[bc1]|bzf|\bbos\b|sprechfunk|funkzeugnis|funk/i.test(l.typ ?? ''))
                return (
                  <>
                    {drohnen.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Send className="h-3 w-3 text-blue-400 shrink-0" />
                        <LizenzNamen liste={drohnen} />
                      </div>
                    )}
                    {fuehrerscheine.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Car className="h-3 w-3 text-emerald-400 shrink-0" />
                        <LizenzNamen liste={fuehrerscheine} />
                      </div>
                    )}
                    {funklizenzen.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Radio className="h-3 w-3 text-violet-400 shrink-0" />
                        <LizenzNamen liste={funklizenzen} />
                      </div>
                    )}
                    {sonstige.length > 0 && (
                      <div className="flex items-center gap-1">
                        <BadgeCheck className="h-3 w-3 text-gray-400 shrink-0" />
                        <LizenzNamen liste={sonstige} />
                      </div>
                    )}
                  </>
                )
              })()}
        </div>
      </div>
      {onEntfernen && (
        <button
          type="button"
          onPointerDown={e => e.stopPropagation()}
          onClick={onEntfernen}
          title={platzhalterText ?? 'Entfernen'}
          className="flex h-8 w-8 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11 shrink-0 items-center justify-center rounded-lg text-dark-text-muted hover:bg-red-500/15 hover:text-red-300"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

/* ------------------------------------------------------------ Seite */

let blockZaehler = 0
const neuerBlock = (): Block => ({ key: `b${++blockZaehler}`, fahrzeugId: null, besatzung: [] })

const Q_SCHMAL = '(max-width: 1099.98px)'
/** Unter ca. 1100 px: Besatzung als zweizeilige Karten, „Verfügbare Kräfte“ als obere Leiste */
function useSchmal() {
  const [s, setS] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(Q_SCHMAL).matches)
  useEffect(() => {
    if (!window.matchMedia) return
    const mq = window.matchMedia(Q_SCHMAL)
    const f = () => setS(mq.matches)
    mq.addEventListener('change', f)
    return () => mq.removeEventListener('change', f)
  }, [])
  return s
}

export function KraefteManagementPage() {
  const schmal = useSchmal()
  const [kraefte, setKraefte] = useState<Kraft[]>([])
  const [fahrzeuge, setFahrzeuge] = useState<Fahrzeug[]>([])
  // Globale Einsatzwahl (Provider in App.tsx): ID als number, Vorauswahl laufend vor geplant macht der Provider
  const { einsatzId } = useEinsatz()
  const [stammNeu, setStammNeu] = useState(0)
  const [ladeNeu, setLadeNeu] = useState(0)
  const [bloecke, setBloecke] = useState<Block[]>([neuerBlock()])
  const [reserveWache, setReserveWache] = useState<number[]>([])
  const [einsatzReserve, setEinsatzReserve] = useState<ReserveEintrag[]>([])
  const [einsatzEnde, setEinsatzEnde] = useState<ReserveEintrag[]>([])
  const [laden, setLaden] = useState(true)
  const [laedtEinsatz, setLaedtEinsatz] = useState(false)
  const [ladeFehler, setLadeFehler] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [speicherStatus, setSpeicherStatus] = useState<'leer' | 'speichert' | 'gespeichert' | 'fehler'>('leer')
  const [zeiten, setZeiten] = useState<Record<string, { beginn: string; ende: string }>>({})
  const [funktionMerker, setFunktionMerker] = useState<Record<string, string>>({})
  const [trupps, setTrupps] = useState<Trupp[]>([])
  const [truppProtokoll, setTruppProtokoll] = useState<TruppProtokoll[]>([])
  const [truppHinweis, setTruppHinweis] = useState<string | null>(null)
  const [neuerTruppTyp, setNeuerTruppTyp] = useState('drohnentrupp')
  // Neu angelegte Trupps ohne Mitglied (werden erst mit dem ersten Mitglied gespeichert; leere Trupps sind sonst aufgelöst)
  const [entwurfTrupps, setEntwurfTrupps] = useState<string[]>([])
  const entwurfRef = useRef<string[]>([])
  entwurfRef.current = entwurfTrupps
  // Zugeklappte Fahrzeuge (pro Fahrzeug, im Browser gemerkt)
  const [zugeklappt, setZugeklappt] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem('km-zugeklappt') || '{}') || {} } catch { return {} }
  })
  const klappeKey = (b: Block) => (b.fahrzeugId ? `fz${b.fahrzeugId}` : b.key)
  const umklappen = (b: Block) => setZugeklappt(z => {
    const n = { ...z, [klappeKey(b)]: !z[klappeKey(b)] }
    try { localStorage.setItem('km-zugeklappt', JSON.stringify(n)) } catch { /* ignorieren */ }
    return n
  })
  // Reiter „Fahrzeuge“ / „Trupps“: URL-Parameter ?reiter=… hat Vorrang, sonst zuletzt gewählter (localStorage)
  const [suchParams, setSuchParams] = useSearchParams()
  const reiter: 'fahrzeuge' | 'trupps' = (() => {
    const q = suchParams.get('reiter')
    if (q === 'trupps' || q === 'fahrzeuge') return q
    try { return localStorage.getItem('km-reiter') === 'trupps' ? 'trupps' : 'fahrzeuge' } catch { return 'fahrzeuge' }
  })()
  const waehleReiter = (k: 'fahrzeuge' | 'trupps') => {
    try { localStorage.setItem('km-reiter', k) } catch { /* ignorieren */ }
    setSuchParams(prev => { const n = new URLSearchParams(prev); n.set('reiter', k); return n }, { replace: true })
  }
  const hydriert = useRef(false)
  /** Einsatz-ID, zu der die Daten im Zustand gehören (beim Laden gemerkt) – Speichern nutzt immer diese */
  const geladeneId = useRef<number | null>(null)
  const ungespeichert = useRef(false)
  const speicherVersion = useRef(0)

  // ---- Drag-State (Pointer-basiert → funktioniert mit Maus, Finger und Stift)
  const [drag, setDrag] = useState<{ nutzerId: number; x: number; y: number; over: string | null } | null>(null)
  const downRef = useRef<{ nutzerId: number; x: number; y: number; moved: boolean } | null>(null)
  const dragRef = useRef<typeof drag>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setFehler(null)
        const [u, f, l] = await Promise.all([
          userApi.list({ size: 500 }),
          vehicleApi.list({ size: 100 }),
          api.get('/lizenzen'),
        ])
        if (cancelled) return
        const lizByNutzer = new Map<number, Lizenz[]>()
        for (const lz of (l.data?.items ?? []) as any[]) {
          if (!lizByNutzer.has(lz.nutzer_id)) lizByNutzer.set(lz.nutzer_id, [])
          lizByNutzer.get(lz.nutzer_id)!.push({ id: lz.id, typ: lz.typ, abgelaufen: lz.abgelaufen, ablaufdatum: lz.ablaufdatum })
        }
        const liste: Kraft[] = ((u.data?.items ?? []) as any[])
          .filter(x => (x.funktionen ?? []).length > 0)      // nur Kräfte mit Funktion am Einsatzort
          .map(x => ({
            id: x.id,
            vorname: x.vorname,
            nachname: x.nachname,
            benutzername: x.benutzername,
            funktionen: x.funktionen ?? [],
            lizenzen: lizByNutzer.get(x.id) ?? [],
          }))
          .sort((a, b) => (`${a.nachname ?? ''}${a.vorname ?? ''}`).localeCompare(`${b.nachname ?? ''}${b.vorname ?? ''}`, 'de'))
        setKraefte(liste)
        setFahrzeuge((f.data?.items ?? []) as Fahrzeug[])
      } catch (e: any) {
        if (!cancelled) setFehler(e?.response?.data?.detail || 'Daten konnten nicht geladen werden')
      } finally {
        if (!cancelled) setLaden(false)
      }
    })()
    return () => { cancelled = true }
  }, [stammNeu])

  // Besatzungsplätze an die im Fahrzeug hinterlegte Stärke anpassen (nur auffüllen, nie Personen verlieren)
  useEffect(() => {
    if (!hydriert.current || fahrzeuge.length === 0) return
    setBloecke(bs => {
      let geaendert = false
      const neu = bs.map(bl => {
        const fz = fahrzeuge.find(f => f.id === bl.fahrzeugId)
        const soll = fz?.max_einsatzkraefte ?? 0
        if (!fz || bl.besatzung.length >= soll) return bl
        geaendert = true
        return { ...bl, besatzung: [...bl.besatzung, ...Array.from({ length: soll - bl.besatzung.length }, () => ({ nutzerId: null, funktion: '', beginn: '', ende: '' }))] }
      })
      return geaendert ? neu : bs
    })
  }, [fahrzeuge, laedtEinsatz, einsatzId])

  const kraftById = useMemo(() => new Map(kraefte.map(k => [k.id, k])), [kraefte])

  /* ------------------------------------------- Daten je Einsatz laden */

  useEffect(() => {
    if (einsatzId == null) {
      hydriert.current = false
      setBloecke([neuerBlock()]); setReserveWache([]); setEinsatzReserve([]); setEinsatzEnde([]); setZeiten({}); setFunktionMerker({}); setTrupps([]); setTruppProtokoll([]); setTruppHinweis(null); setEntwurfTrupps([])
      setSpeicherStatus('leer'); setLadeFehler(null)
      return
    }
    let cancelled = false
    hydriert.current = false
    setLaedtEinsatz(true)
    setLadeFehler(null)
    const ladeId = einsatzId
    api.get(`/kraefte-management/${ladeId}`)
      .then(r => {
        if (cancelled) return
        const d: KMData = holeKmData(r) as unknown as KMData
        setBloecke(Array.isArray(d.bloecke) && d.bloecke.length
          ? d.bloecke.map(b => ({ ...b, key: b.key || `b${++blockZaehler}` }))
          : [neuerBlock()])
        setReserveWache(Array.isArray(d.reserveWache) ? d.reserveWache : [])
        setEinsatzReserve(Array.isArray(d.einsatzReserve) ? d.einsatzReserve : [])
        setEinsatzEnde(Array.isArray(d.einsatzEnde) ? d.einsatzEnde : [])
        setTrupps(Array.isArray(d.trupps) ? d.trupps : [])
        setTruppProtokoll(Array.isArray(d.truppProtokoll) ? d.truppProtokoll : [])
        setTruppHinweis(null)
        setZeiten(d.zeiten && typeof d.zeiten === 'object' ? d.zeiten : {})
        setFunktionMerker(d.funktionMerker && typeof d.funktionMerker === 'object' ? d.funktionMerker : {})
        setSpeicherStatus(r.data?.aktualisiert_am ? 'gespeichert' : 'leer')
        // WICHTIG: Autosave erst nach ERFOLGREICHEM Laden freigeben – sonst
        // überschreibt ein fehlgeschlagener Load (401/502) die Einteilung mit einem leeren Stand.
        hydriert.current = true
        geladeneId.current = ladeId
        ungespeichert.current = false
      })
      .catch(() => {
        if (cancelled) return
        hydriert.current = false
        setLadeFehler('Die Einteilung für diesen Einsatz konnte nicht geladen werden.')
      })
      .finally(() => { if (!cancelled) setLaedtEinsatz(false) })
    return () => { cancelled = true }
  }, [einsatzId, ladeNeu])

  // „Erneut versuchen“: Stammdaten neu, Einteilung nur neu laden, wenn sie nicht geladen werden konnte (sonst gingen Änderungen verloren)
  useConnectionRetry(() => { if (fehler) setStammNeu(n => n + 1); if (ladeFehler) setLadeNeu(n => n + 1) })

  /* --------------------------- automatisches Speichern (entprellt) */

  useEffect(() => {
    if (einsatzId == null || !hydriert.current || ladeFehler) return
    const ziel = einsatzId, version = ++speicherVersion.current
    ungespeichert.current = true
    const t = setTimeout(async () => {
      setSpeicherStatus('speichert')
      try {
        const res = await api.put(`/kraefte-management/${ziel}`, {
          data: { bloecke, reserveWache, einsatzReserve, einsatzEnde, zeiten, funktionMerker, trupps },
        })
        // Das Protokoll erzeugt der Server (Diff alt -> neu); hier nur anzeigen
        if (speicherVersion.current === version && Array.isArray(res.data?.truppProtokoll)) setTruppProtokoll(res.data.truppProtokoll)
        if (speicherVersion.current === version) {
          ungespeichert.current = false
          // Der Server hat leere Trupps aufgelöst -> lokal ebenfalls entfernen (außer neuen Entwürfen)
          setTrupps(ts => ts.some(t => t.mitglieder.length === 0 && !entwurfRef.current.includes(t.key)) ? ts.filter(t => t.mitglieder.length > 0 || entwurfRef.current.includes(t.key)) : ts)
        }
        setSpeicherStatus('gespeichert')
      } catch {
        setSpeicherStatus('fehler')
      }
    }, 800)
    return () => clearTimeout(t)
  }, [einsatzId, bloecke, reserveWache, einsatzReserve, einsatzEnde, zeiten, funktionMerker, trupps, ladeFehler])

  // Einsatzwechsel: ausstehende Änderungen vorher speichern (mit der beim Laden gemerkten ID); bei Fehler nachfragen/abbrechen
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung()
  useEinsatzWechsel(async () => {
    if (!hydriert.current || ladeFehler || !ungespeichert.current || geladeneId.current == null) return
    const ziel = geladeneId.current, version = speicherVersion.current
    setSpeicherStatus('speichert')
    try {
      await api.put(`/kraefte-management/${ziel}`, { data: { bloecke, reserveWache, einsatzReserve, einsatzEnde, zeiten, funktionMerker, trupps } })
      if (speicherVersion.current === version) ungespeichert.current = false
      setSpeicherStatus('gespeichert')
    } catch {
      setSpeicherStatus('fehler')
      return bestaetigen({ titel: 'Einteilung nicht gespeichert', text: 'Die Einteilung konnte nicht gespeichert werden. Trotzdem den Einsatz wechseln und die letzten Änderungen verwerfen?', bestaetigenText: 'Trotzdem wechseln', abbrechenText: 'Hier bleiben' })
    }
  })

  /* ---------------------------------------------------- Platzierung */

  const entferneUeberall = useCallback((nutzerId: number) => {
    setBloecke(b => b.map(bl => bl.besatzung.some(r => r.nutzerId === nutzerId)
      ? { ...bl, besatzung: bl.besatzung.map(r => r.nutzerId === nutzerId ? { ...r, nutzerId: null, beginn: '', ende: '', funktion: '' } : r) }
      : bl))
    setReserveWache(r => r.filter(id => id !== nutzerId))
    setEinsatzReserve(r => r.filter(x => x.nutzerId !== nutzerId))
    setEinsatzEnde(r => r.filter(x => x.nutzerId !== nutzerId))
  }, [])

  const platziere = useCallback((nutzerId: number, zielRoh: string) => {
    let ziel = zielRoh
    // 1) Find current times of this person before removing
    let times = { beginn: '', ende: '' }
    // Einsatzfunktion folgt der Person: aktuelle Funktion im Fahrzeug, sonst die zuletzt bekannte
    let funktion = funktionMerker[String(nutzerId)] || ''
    
    // Check crew positions
    for (const bl of bloecke) {
      const row = bl.besatzung.find(r => r.nutzerId === nutzerId)
      if (row) {
        times = { beginn: row.beginn || '', ende: row.ende || '' }
        funktion = row.funktion || ''
        break
      }
    }
    // Check einsatzReserve (has verfuegbarAb as beginn)
    if (!times.beginn && !times.ende) {
      const reserveEntry = einsatzReserve.find(x => x.nutzerId === nutzerId)
      if (reserveEntry) {
        times = { beginn: reserveEntry.verfuegbarAb || '', ende: '' }
      }
    }
    // Check einsatzEnde
    if (!times.beginn && !times.ende) {
      const endeEntry = einsatzEnde.find(x => x.nutzerId === nutzerId)
      if (endeEntry) {
        times = { beginn: endeEntry.beginn || '', ende: endeEntry.ende || '' }
      }
    }

    // Zugeklapptes Fahrzeug als Ziel: ersten freien Platz nehmen (ohne Platz: nichts tun)
    if (ziel.startsWith('block:')) {
      const bl = bloecke.find(x => x.key === ziel.slice(6))
      const frei = bl?.besatzung.findIndex(r => r.nutzerId == null) ?? -1
      if (!bl || frei < 0) return
      ziel = `crew:${bl.key}:${frei}`
    }

    // Tausch: Ziel-Platz belegt und die Kraft steht selbst auf einem Besatzungsplatz -> beide tauschen Platz und Funktion
    if (ziel.startsWith('crew:')) {
      const [, zKey, zIdxRaw] = ziel.split(':')
      const zIdx = Number(zIdxRaw)
      const zielZeile = bloecke.find(b => b.key === zKey)?.besatzung[zIdx]
      let quelle: { key: string; idx: number } | null = null
      for (const bl of bloecke) {
        const idx = bl.besatzung.findIndex(r => r.nutzerId === nutzerId)
        if (idx >= 0) { quelle = { key: bl.key, idx }; break }
      }
      if (quelle && zielZeile && zielZeile.nutzerId != null) {
        if (zielZeile.nutzerId === nutzerId) return
        const q = quelle
        setBloecke(b => {
          const qRow = b.find(x => x.key === q.key)!.besatzung[q.idx]
          const zRow = b.find(x => x.key === zKey)!.besatzung[zIdx]
          return b.map(bl => ({
            ...bl,
            besatzung: bl.besatzung.map((r, i) =>
              bl.key === q.key && i === q.idx ? { ...zRow }
              : bl.key === zKey && i === zIdx ? { ...qRow }
              : r),
          }))
        })
        return
      }
    }

    // 2) Remove from all places (Funktion merken, damit sie bei der Rückkehr ins Fahrzeug wiederhergestellt wird)
    entferneUeberall(nutzerId)
    if (ziel.startsWith('crew:')) {
      if (funktion) setFunktionMerker(m => ({ ...m, [String(nutzerId)]: funktion }))
    } else if (ziel === 'reserve' || ziel === 'einsatzreserve' || ziel === 'einsatzende') {
      if (funktion) setFunktionMerker(m => ({ ...m, [String(nutzerId)]: funktion }))
    } else {
      setFunktionMerker(m => { if (!(String(nutzerId) in m)) return m; const { [String(nutzerId)]: _x, ...rest } = m; return rest })
    }

    // 3) Place at new location with preserved times
    if (ziel === 'reserve') {
      setReserveWache(r => (r.includes(nutzerId) ? r : [...r, nutzerId]))
    } else if (ziel === 'einsatzreserve') {
      const entry: ReserveEintrag = { nutzerId, verfuegbarAb: times.beginn }
      setEinsatzReserve(r => (r.some(x => x.nutzerId === nutzerId) ? r : [...r, entry]))
    } else if (ziel === 'einsatzende') {
      const entry: ReserveEintrag = { nutzerId, verfuegbarAb: times.beginn, beginn: times.beginn, ende: times.ende }
      // Allow multiple entries for same person (different time periods)
      setEinsatzEnde(r => [...r, entry])
    } else if (ziel.startsWith('crew:')) {
      const [, key, idxRaw] = ziel.split(':')
      const idx = Number(idxRaw)
      setBloecke(b => b.map(bl => {
        if (bl.key !== key) return bl
        const besatzung = bl.besatzung.map((r, i) => {
          if (i === idx) return { ...r, nutzerId, beginn: times.beginn, ende: times.ende, funktion }
          if (r.nutzerId === nutzerId) return { ...r, nutzerId: null, beginn: '', ende: '', funktion: '' }
          return r
        })
        return { ...bl, besatzung }
      }))
    }
  }, [entferneUeberall, bloecke, einsatzReserve, einsatzEnde, funktionMerker])

  /* -------------------------------------------------------- DnD-Handler */

  const starteDrag = useCallback((e: React.PointerEvent, nutzerId: number) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    downRef.current = { nutzerId, x: e.clientX, y: e.clientY, moved: false }
  }, [])

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = downRef.current
      if (!d) return
      if (!d.moved && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 8) return
      d.moved = true
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null
      const zielEl = el?.closest('[data-drop]') as HTMLElement | null
      const next = { nutzerId: d.nutzerId, x: e.clientX, y: e.clientY, over: zielEl?.dataset.drop ?? null }
      dragRef.current = next
      setDrag(next)
    }
    const onUp = () => {
      const d = downRef.current
      const cur = dragRef.current
      downRef.current = null
      dragRef.current = null
      setDrag(null)
      if (d?.moved && cur?.over) platziere(cur.nutzerId, cur.over)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [platziere])

  /* --------------------------------------------------- Fahrzeug-Blöcke */

  const setzeFahrzeug = (key: string, fahrzeugId: number | null) => {
    const fz = fahrzeuge.find(f => f.id === fahrzeugId)
    const plaetze = Math.max(0, fz?.max_einsatzkraefte ?? 0)
    setBloecke(b => b.map(bl => {
      if (bl.key !== key) return bl
      // bereits zugewiesene Kräfte behalten
      const besatzung: CrewRow[] = Array.from({ length: plaetze }, (_, i) =>
        bl.besatzung[i] ?? { nutzerId: null, funktion: '', beginn: '', ende: '' })
      return { ...bl, fahrzeugId, besatzung }
    }))
  }

  const autoBeginn = () => new Date().toTimeString().slice(0, 5)

  const autoEnde = () => new Date().toTimeString().slice(0, 5)

  /* --------------------------------------------- bereits platzierte IDs */

  const platziert = useMemo(() => {
    const s = new Set<number>()
    bloecke.forEach(b => b.besatzung.forEach(r => { if (r.nutzerId) s.add(r.nutzerId) }))
    reserveWache.forEach(id => s.add(id))
    einsatzReserve.forEach(x => s.add(x.nutzerId))
    return s
  }, [bloecke, reserveWache, einsatzReserve])

  const verfuegbar = useMemo(() => kraefte.filter(k => !platziert.has(k.id)), [kraefte, platziert])

  const fahrzeugLabel = (f: Fahrzeug) =>
    `${f.rufzeichen ?? f.kennzeichen ?? '#' + f.id}${f.fahrzeugtyp ? ` – ${f.fahrzeugtyp}` : (f.typ ? ` – ${f.typ}` : '')}`

  const dragKraft = drag ? kraftById.get(drag.nutzerId) : null

  /* ------------------------------------------------------------ Trupps */

  /** Einsatzkräfte aus den Fahrzeugblöcken (nur sie können in Trupps) mit Fahrzeug-Hinweis */
  const imFahrzeug = useMemo(() => {
    const m = new Map<number, string>()
    for (const bl of bloecke) {
      const fz = fahrzeuge.find(f => f.id === bl.fahrzeugId)
      const label = fz ? (fz.rufzeichen ?? fz.kennzeichen ?? `#${fz.id}`) : 'Fahrzeug'
      for (const r of bl.besatzung) if (r.nutzerId) m.set(r.nutzerId, label)
    }
    return m
  }, [bloecke, fahrzeuge])

  const truppVonKraft = useMemo(() => {
    const m = new Map<number, Trupp>()
    for (const t of trupps) for (const id of t.mitglieder) m.set(id, t)
    return m
  }, [trupps])

  const kraftName = (id: number) => {
    const k = kraftById.get(id)
    return k ? (`${k.vorname ?? ''} ${k.nachname ?? ''}`.trim() || k.benutzername) : `#${id}`
  }

  // Wer nicht mehr in einem Fahrzeugblock steht, verlässt den Trupp (der Server macht dasselbe als Absicherung).
  useEffect(() => {
    if (!hydriert.current) return
    setTrupps(ts => {
      let geaendert = false
      const neu = ts.map(t => {
        const rest = t.mitglieder.filter(id => (t.reserve ? reserveWache.includes(id) : imFahrzeug.has(id)))
        if (rest.length === t.mitglieder.length) return t
        geaendert = true
        return { ...t, mitglieder: rest, fuehrerId: rest.length === 0 ? null : (t.fuehrerId != null && rest.includes(t.fuehrerId) ? t.fuehrerId : rest[0]) }
      })
      return geaendert ? neu : ts
    })
  }, [imFahrzeug, reserveWache])

  const neuerTrupp = () => {
    const typ = neuerTruppTyp
    const label = truppTypLabel(typ)
    const nutzt = new Set(trupps.map(t => t.name))
    let n = trupps.filter(t => t.typ === typ).length + 1
    while (nutzt.has(`${label} ${n}`)) n++
    // Schlüssel nie wiederverwenden (auch nicht von aufgelösten Trupps, die nur noch im Protokoll stehen)
    const zahlen = [...trupps.map(t => t.key), ...truppProtokoll.map(p => p.truppKey)].map(k => Number(String(k).slice(1))).filter(Number.isFinite)
    setTrupps(ts => [...ts, { key: `t${Math.max(0, ...zahlen) + 1}`, name: `${label} ${n}`, typ, fuehrerId: null, mitglieder: [] }])
    setEntwurfTrupps(e => [...e, `t${Math.max(0, ...zahlen) + 1}`])
    setTruppHinweis(null)
  }

  const aendereTrupp = (key: string, f: (t: Trupp) => Trupp) => setTrupps(ts => ts.map(t => t.key === key ? f(t) : t))

  const truppMitgliedHinzu = (key: string, nutzerId: number) => {
    const ziel = trupps.find(t => t.key === key)
    if (!ziel || ziel.mitglieder.includes(nutzerId) || ziel.mitglieder.length >= TRUPP_MAX) return
    const alt = truppVonKraft.get(nutzerId)
    setTrupps(ts => ts.map(t => {
      if (t.key === key) {
        const mitglieder = [...t.mitglieder, nutzerId]
        setEntwurfTrupps(e => e.filter(k => k !== key))
        return { ...t, mitglieder, fuehrerId: t.fuehrerId ?? nutzerId }
      }
      if (t.mitglieder.includes(nutzerId)) {
        const rest = t.mitglieder.filter(id => id !== nutzerId)
        return { ...t, mitglieder: rest, fuehrerId: rest.length === 0 ? null : (t.fuehrerId === nutzerId ? rest[0] : t.fuehrerId) }
      }
      return t
    }))
    setTruppHinweis(alt ? `${kraftName(nutzerId)} wurde aus „${alt.name}“ entfernt und „${ziel.name}“ zugeordnet (eine Person ist nur in einem Trupp).` : null)
  }

  const truppMitgliedEntfernen = (key: string, nutzerId: number) => {
    aendereTrupp(key, t => {
      const rest = t.mitglieder.filter(id => id !== nutzerId)
      return { ...t, mitglieder: rest, fuehrerId: rest.length === 0 ? null : (t.fuehrerId === nutzerId ? rest[0] : t.fuehrerId) }
    })
    setTruppHinweis(null)
  }

  const truppAufloesen = async (t: Trupp) => {
    const ok = await bestaetigen({ titel: 'Trupp auflösen', text: `„${t.name}“ wirklich auflösen? Die Fahrzeugzuordnung der Mitglieder bleibt unverändert.`, bestaetigenText: 'Auflösen', abbrechenText: 'Abbrechen' })
    if (!ok) return
    setTrupps(ts => ts.filter(x => x.key !== t.key))
    setTruppHinweis(null)
  }

  /** Alle Mitglieder gemeinsam aus den Fahrzeugblöcken in die Reserve Wache; Herkunft (Block, Funktion) wird im Trupp gemerkt. */
  const truppInReserve = (t: Trupp) => {
    if (t.reserve || t.mitglieder.length === 0) return
    const herkunft: TruppHerkunft[] = []
    for (const id of t.mitglieder) {
      for (const bl of bloecke) {
        const row = bl.besatzung.find(r => r.nutzerId === id)
        if (row) { herkunft.push({ nutzerId: id, blockKey: bl.key, fahrzeugId: bl.fahrzeugId, funktion: row.funktion || '' }); break }
      }
    }
    const ids = new Set(t.mitglieder)
    setFunktionMerker(m => { const n = { ...m }; for (const h of herkunft) if (h.funktion) n[String(h.nutzerId)] = h.funktion; return n })
    setBloecke(b => b.map(bl => bl.besatzung.some(r => r.nutzerId != null && ids.has(r.nutzerId))
      ? { ...bl, besatzung: bl.besatzung.map(r => r.nutzerId != null && ids.has(r.nutzerId) ? { ...r, nutzerId: null, beginn: '', ende: '', funktion: '' } : r) }
      : bl))
    setReserveWache(r => [...r, ...t.mitglieder.filter(id => !r.includes(id))])
    setEinsatzReserve(r => r.filter(x => !ids.has(x.nutzerId)))
    setEinsatzEnde(r => r.filter(x => !ids.has(x.nutzerId)))
    aendereTrupp(t.key, x => ({ ...x, reserve: { seit: new Date().toISOString(), herkunft } }))
    setTruppHinweis(null)
  }

  /** Alle Mitglieder zurück in ihre bisherigen Fahrzeugblöcke (mit Funktion); ohne Block/freien Platz -> „Verfügbare Kräfte“. */
  const truppZurueckInEinsatz = (t: Trupp) => {
    if (!t.reserve) return
    const herk = new Map(t.reserve.herkunft.map(h => [h.nutzerId, h]))
    const offen: string[] = []
    const gesetzt = new Set<number>()
    const neueBloecke = bloecke.map(bl => ({ ...bl, besatzung: bl.besatzung.map(r => ({ ...r })) }))
    for (const id of t.mitglieder) {
      const h = herk.get(id)
      const bl = h ? neueBloecke.find(x => x.key === h.blockKey) : undefined
      const slot = bl?.besatzung.find(r => r.nutzerId == null)
      if (h && slot) { slot.nutzerId = id; slot.funktion = h.funktion; slot.beginn = ''; slot.ende = ''; gesetzt.add(id) }
      else offen.push(kraftName(id))
    }
    setBloecke(neueBloecke)
    setReserveWache(r => r.filter(id => !t.mitglieder.includes(id)))
    aendereTrupp(t.key, x => { const { reserve: _r, ...rest } = x; return rest })
    setTruppHinweis(offen.length ? `${offen.join(', ')}: Fahrzeug oder freier Platz nicht mehr vorhanden – unter „Verfügbare Kräfte“ abgelegt, verlassen den Trupp.` : null)
  }

  const aktionText = (p: TruppProtokoll) => {
    const wer = p.nutzerId != null ? kraftName(p.nutzerId) : ''
    switch (p.aktion) {
      case 'GEBILDET': return `${p.truppName} gebildet`
      case 'AUFGELOEST': return `${p.truppName} aufgelöst`
      case 'HINZU': return `${wer} zu ${p.truppName} hinzugefügt`
      case 'ENTFERNT': return `${wer} aus ${p.truppName} entfernt`
      case 'FUEHRER': return `Truppführer ${p.truppName}: ${p.alt != null ? kraftName(Number(p.alt)) : '–'} → ${p.neu != null ? kraftName(Number(p.neu)) : '–'}`
      case 'UMBENANNT': return `${p.alt} umbenannt in ${p.neu}`
      case 'TYP': return `${p.truppName}: Typ ${truppTypLabel(String(p.alt))} → ${truppTypLabel(String(p.neu))}`
      case 'RESERVE': return `${p.truppName} in Reserve Wache`
      case 'EINSATZ': return `${p.truppName} zurück im Einsatz`
      default: return p.aktion
    }
  }

  /* ------------------------------------------------------------ Render */

  const grid = 'grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(176px,200px)_minmax(176px,200px)] gap-2'

  const reiterListe: Array<ReiterEintrag<'fahrzeuge' | 'trupps'>> = [
    { key: 'fahrzeuge', label: 'Fahrzeuge' },
    { key: 'trupps', label: `Trupps${trupps.filter(t => t.mitglieder.length > 0).length ? ` (${trupps.filter(t => t.mitglieder.length > 0).length})` : ''}` },
  ]

  const truppsKarte = (
            <>
            <Card className="p-4" data-trupps>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="flex items-center gap-2 text-base font-semibold text-dark-text">
                  <Users className="h-4 w-4 text-primary-400" /> Trupps
                </h3>
                <div className="flex w-full items-center gap-2 sm:w-auto">
                  <div className="min-w-0 flex-1 sm:w-56 sm:flex-none">
                    <Select value={neuerTruppTyp} onChange={e => setNeuerTruppTyp(e.target.value)} options={TRUPP_TYPEN} />
                  </div>
                  <Button variant="secondary" className="shrink-0 whitespace-nowrap" data-trupp-anlegen onClick={neuerTrupp}>
                    <Plus className="h-4 w-4 mr-1" /> Trupp anlegen
                  </Button>
                </div>
              </div>
              <p className="mt-1 text-xs text-dark-text-muted">
                Ein Trupp hat höchstens {TRUPP_MAX} Mitglieder und einen Truppführer. Es zählen nur Kräfte aus den Fahrzeugen; „Trupp in Reserve“ verschiebt alle Mitglieder gemeinsam in die Reserve Wache. Ein Trupp ohne Mitglieder ist aufgelöst.
              </p>
              {truppHinweis && (
                <div data-trupp-hinweis className="mt-3 flex items-start justify-between gap-2 rounded-xl border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
                  <span>{truppHinweis}</span>
                  <button type="button" onClick={() => setTruppHinweis(null)} className="shrink-0 text-amber-200/80 hover:text-amber-100"><X className="h-4 w-4" /></button>
                </div>
              )}

              {trupps.filter(t => t.mitglieder.length > 0 || entwurfTrupps.includes(t.key)).length === 0 ? (
                <p className="mt-4 text-center text-xs text-dark-text-subtle">Noch keine Trupps.</p>
              ) : (
                <div className="mt-4 grid gap-4 xl:grid-cols-2">
                  {trupps.filter(t => t.mitglieder.length > 0 || entwurfTrupps.includes(t.key)).map(t => {
                    const waehlbar = [...imFahrzeug.keys()].filter(id => !t.mitglieder.includes(id))
                    return (
                      <div key={t.key} data-trupp={t.key} data-trupp-reserve={t.reserve ? 'true' : undefined}
                        className={`rounded-2xl border p-3 ${t.reserve ? 'border-dashed border-amber-400/40 bg-dark-bg/60 opacity-90' : 'border-dark-border bg-dark-bg'}`}>
                        <div className="flex items-center gap-2">
                          <Input value={t.name} maxLength={60} aria-label="Truppname" className="min-w-0"
                            onChange={e => aendereTrupp(t.key, x => ({ ...x, name: e.target.value }))} />
                          {t.reserve && <Badge variant="warning" data-trupp-status>Reserve</Badge>}
                          <Badge variant={t.mitglieder.length >= TRUPP_MAX ? 'warning' : 'info'}>{t.mitglieder.length}/{TRUPP_MAX}</Badge>
                          <button type="button" data-trupp-aufloesen title="Trupp auflösen" onClick={() => truppAufloesen(t)}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-dark-text-muted hover:bg-red-500/15 hover:text-red-300">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="mt-2">
                          <Select value={t.typ} aria-label="Trupptyp" onChange={e => aendereTrupp(t.key, x => ({ ...x, typ: e.target.value }))} options={TRUPP_TYPEN} />
                        </div>
                        <div className="mt-3 space-y-2">
                          {t.mitglieder.length === 0 && <p className="text-xs text-dark-text-subtle">Keine Mitglieder.</p>}
                          {t.mitglieder.map(id => (
                            <div key={id} data-trupp-mitglied={id} className="flex items-center gap-2 rounded-xl border border-dark-border bg-dark-bg-tertiary px-2.5 py-2">
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium text-dark-text">{kraftName(id)}</div>
                                <div className="truncate text-xs text-dark-text-subtle">{t.reserve ? 'Reserve Wache' : (imFahrzeug.get(id) ?? 'nicht im Fahrzeug')}</div>
                              </div>
                              <button type="button" data-trupp-tf={id}
                                title={t.fuehrerId === id ? 'Truppführer' : 'Zum Truppführer machen'}
                                onClick={() => aendereTrupp(t.key, x => ({ ...x, fuehrerId: id }))}
                                className={`flex h-9 items-center gap-1 rounded-lg px-2 text-xs font-medium [@media(pointer:coarse)]:h-11 ${t.fuehrerId === id ? 'bg-amber-400/20 text-amber-200' : 'text-dark-text-muted hover:bg-dark-bg'}`}>
                                <Star className={`h-4 w-4 ${t.fuehrerId === id ? 'fill-current' : ''}`} /> {t.fuehrerId === id ? 'TF' : ''}
                              </button>
                              <button type="button" title="Aus dem Trupp entfernen" onClick={() => truppMitgliedEntfernen(t.key, id)}
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-dark-text-muted hover:bg-red-500/15 hover:text-red-300 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {t.reserve ? (
                            <Button variant="secondary" data-trupp-zurueck onClick={() => truppZurueckInEinsatz(t)}>Zurück in den Einsatz</Button>
                          ) : t.mitglieder.length > 0 && (
                            <Button variant="secondary" data-trupp-reserve-knopf onClick={() => truppInReserve(t)}>Trupp in Reserve</Button>
                          )}
                        </div>
                        {!t.reserve && <div className="mt-3">
                          <Select value="" aria-label="Mitglied hinzufügen" data-trupp-add={t.key}
                            disabled={t.mitglieder.length >= TRUPP_MAX || waehlbar.length === 0}
                            onChange={e => { if (e.target.value) truppMitgliedHinzu(t.key, Number(e.target.value)) }}
                            placeholder={t.mitglieder.length >= TRUPP_MAX ? 'Trupp ist voll' : '+ Mitglied hinzufügen'}
                            options={waehlbar.map(id => {
                              const anderer = truppVonKraft.get(id)
                              return { value: String(id), label: `${kraftName(id)} · ${imFahrzeug.get(id)}${anderer ? ` (aus „${anderer.name}“)` : ''}` }
                            })} />
                        </div>}
                      </div>
                    )
                  })}
                </div>
              )}

              <details className="mt-4 rounded-xl border border-dark-border" data-trupp-protokoll>
                <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-dark-text-subtle">
                  <ScrollText className="h-4 w-4" /> Protokoll ({truppProtokoll.length})
                </summary>
                <div className="max-h-64 overflow-y-auto border-t border-dark-border px-3 py-2">
                  {truppProtokoll.length === 0
                    ? <p className="text-xs text-dark-text-subtle">Noch keine Änderungen.</p>
                    : truppProtokoll.map((p, i) => (
                        <div key={i} className="flex gap-3 py-0.5 text-xs">
                          <span className="shrink-0 font-mono text-dark-text-subtle">{new Date(p.zeit).toLocaleTimeString('de-DE')}</span>
                          <span className="text-dark-text">{aktionText(p)}</span>
                        </div>
                      ))}
                </div>
              </details>
            </Card>
            </>
  )

  return (
    <div className="space-y-5" data-kraefte-management>
      {bestaetigungsDialog}

      {fehler && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{fehler} <button type="button" className="underline" onClick={() => setStammNeu(n => n + 1)}>Erneut versuchen</button></div>
      )}

      {/* Einsatz wählt die Kopfzeile (global); hier nur Fensterknopf und Speicherstatus */}
      <div className="flex min-h-[1.5rem] items-center justify-end gap-3 text-xs">
        {speicherStatus === 'speichert' && <span className="text-dark-text-muted">Speichert…</span>}
        {speicherStatus === 'gespeichert' && <span className="text-emerald-400">Gespeichert</span>}
        {speicherStatus === 'fehler' && <span className="text-red-300">Speichern fehlgeschlagen</span>}
        {speicherStatus === 'leer' && einsatzId && <span className="text-dark-text-subtle">Noch keine Einteilung</span>}
        <FensterKnopf ansicht="kraefte" />
      </div>

      {laden || laedtEinsatz ? (
        <div className="py-16 text-center text-dark-text-muted">{laden ? 'Lade Kräfte…' : 'Lade Einteilung…'}</div>
      ) : ladeFehler ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-red-300">{ladeFehler}</p>
          <p className="mt-1 text-xs text-dark-text-subtle">
            Zum Schutz der Daten wird nicht gespeichert.
          </p>
          <Button className="mt-3" onClick={() => setLadeNeu(n => n + 1)}>Erneut versuchen</Button>
        </Card>
      ) : einsatzId == null ? (
        <RequireEinsatz hinweis="Bitte zuerst einen Einsatz wählen – danach kann die Einteilung vorgenommen werden.">{() => null}</RequireEinsatz>
      ) : (
        <div className="space-y-4">
        <Reiterleiste reiter={reiterListe} aktiv={reiter} onWechsel={waehleReiter} testid="kraefte-reiter" />
        {reiter === 'trupps' ? truppsKarte : (
        <div className={schmal ? 'space-y-4' : 'grid gap-5 lg:grid-cols-[320px_minmax(0,1fr)]'}>
          {/* ---------------- linke Spalte: Kräfteliste ---------------- */}
          <Card className={schmal ? 'sticky top-0 z-[1] p-3' : 'p-4 h-fit lg:sticky lg:top-4'} data-testid="verfuegbare-kraefte">
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-dark-text">
                <Users className="h-4 w-4 text-primary-400" /> Verfügbare Kräfte
              </h3>
              <Badge variant="info">{verfuegbar.length}</Badge>
            </div>
            <p className="mt-1 text-xs text-dark-text-muted">
              Karte auf ein Feld ziehen (Finger, Stift oder Maus).
            </p>
            <div className={schmal ? 'mt-2 grid max-h-[26vh] grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2' : 'mt-3 space-y-2 max-h-[60vh] overflow-y-auto pr-1'}>
              {verfuegbar.length === 0
                ? <p className="py-6 text-center text-xs text-dark-text-subtle">Alle Kräfte sind eingeplant.</p>
                : verfuegbar.map(k => (
                    <KraftKarte key={k.id} kraft={k} onDragStart={starteDrag} />
                  ))}
            </div>
          </Card>

          {/* ---------------- rechte Spalte ---------------- */}
          <div className="space-y-5 min-w-0">
            {/* ============ Im Einsatz ============ */}
            <Card className="p-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-base font-semibold text-dark-text">Im Einsatz</h3>
                <Button variant="secondary" onClick={() => setBloecke(b => [...b, neuerBlock()])}>
                  <Plus className="h-4 w-4 mr-1" /> Fahrzeug
                </Button>
              </div>

              <div className="mt-4 space-y-4">
                {bloecke.map(block => (
                  <div key={block.key} className="rounded-2xl border border-dark-border overflow-hidden">
                    {/* Kopfzeile: Fahrzeug-Dropdown */}
                    <div data-drop={`block:${block.key}`} data-fahrzeug-kopf className={`flex items-center gap-3 px-3 py-3 ${drag?.over === `block:${block.key}` ? 'bg-primary-600/20' : 'bg-dark-bg-tertiary'}`}>
                      <button type="button" data-fahrzeug-klappen aria-expanded={!zugeklappt[klappeKey(block)]}
                        title={zugeklappt[klappeKey(block)] ? 'Aufklappen' : 'Zuklappen'} onClick={() => umklappen(block)}
                        className="flex h-11 w-9 shrink-0 items-center justify-center rounded-lg text-dark-text-muted hover:bg-dark-bg">
                        {zugeklappt[klappeKey(block)] ? <ChevronRight className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </button>
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-600/15 text-primary-300">
                        <Truck className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <Select
                          value={block.fahrzeugId ? String(block.fahrzeugId) : ''}
                          onChange={e => setzeFahrzeug(block.key, e.target.value ? Number(e.target.value) : null)}
                          placeholder="— Fahrzeug wählen —"
                          options={fahrzeuge.map(f => ({ value: String(f.id), label: `${fahrzeugLabel(f)} (max. ${f.max_einsatzkraefte ?? 0})` }))}
                        />
                      </div>
                      {block.fahrzeugId && (
                        <Badge variant="info" data-fahrzeug-anzahl>{block.besatzung.filter(r => r.nutzerId != null).length}/{block.besatzung.length}</Badge>
                      )}
                      {bloecke.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setBloecke(b => b.filter(x => x.key !== block.key))}
                          title="Fahrzeug entfernen"
                          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-dark-text-muted hover:bg-red-500/15 hover:text-red-300"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {/* Besatzungszeilen (Baumansicht, eingerückt) */}
                    {block.fahrzeugId && !zugeklappt[klappeKey(block)] && (
                      <div className={schmal ? 'min-w-0 px-3 py-3' : 'min-w-0 overflow-x-auto px-3 py-3'}><div className={schmal ? 'space-y-2' : 'min-w-[820px] space-y-2'}>
                        {!schmal && <div className={`${grid} px-1 text-xs font-semibold uppercase tracking-wider text-dark-text-subtle`}>
                          <span>Besatzung</span><span>Funktion</span><span>Beginn</span><span>Ende</span>
                        </div>}
                        {block.besatzung.map((row, i) => {
                          const dropId = `crew:${block.key}:${i}`
                          const istUeberzogen = drag?.over === dropId
                          const kraft = row.nutzerId ? kraftById.get(row.nutzerId) : null
                          const ersteZeile = i === 0
                          return (
                            <div
                              key={i}
                              className={
                                `${schmal ? 'flex flex-col gap-2' : `${grid} items-center ml-4`} rounded-xl px-2 py-2 border
                                ${ersteZeile
                                  ? 'bg-amber-100/90 dark:bg-amber-300/15 border-amber-300/50'
                                  : 'bg-dark-bg border-dark-border'}`
                              }
                            >
                              {/* Spalte 1: Drop-Ziel */}
                              <div data-drop={dropId} className="min-w-0">
                                {kraft ? (
                                  <KraftKarte
                                    kraft={kraft}
                                    klein
                                    ueberzogen={istUeberzogen}
                                    onDragStart={starteDrag}
                                    onEntfernen={() => entferneUeberall(kraft.id)}
                                    platzhalterText="Aus Besatzung entfernen"
                                    funktion={row.funktion}
                                    truppBadge={(() => { const t = truppVonKraft.get(kraft.id); return t ? `${t.name}${t.fuehrerId === kraft.id ? ' · TF' : ''}` : undefined })()}
                                  />
                                ) : (
                                  <div className={
                                    `flex h-12 items-center justify-center rounded-xl border border-dashed text-xs transition-colors
                                    ${istUeberzogen
                                      ? 'border-primary-500 bg-primary-600/20 text-primary-200'
                                      : 'border-dark-border text-dark-text-subtle'}`
                                  }>
                                    {istUeberzogen ? 'Hier ablegen' : `Platz ${i + 1}`}
                                  </div>
                                )}
                              </div>

                              {/* Spalte 2: Funktion */}
                              {schmal ? (
                                <div className="flex min-w-0 flex-wrap items-center gap-1.5" data-testid="funktion-umschalter">
                                  {(['PILOT', 'CO_PILOT'] as const).map(fv => (
                                    <button key={fv} type="button" aria-pressed={row.funktion === fv}
                                      onClick={() => setBloecke(b => b.map(bl => bl.key !== block.key ? bl : {
                                        ...bl, besatzung: bl.besatzung.map((r, j) => j === i ? { ...r, funktion: fv } : r)
                                      }))}
                                      className={`min-h-[44px] flex-1 rounded-lg border px-3 text-sm font-semibold ${row.funktion === fv ? 'border-primary-400 bg-primary-500/25 text-dark-text' : 'border-dark-border text-dark-text-muted'}`}>
                                      {fv === 'PILOT' ? 'Pilot' : 'Co-Pilot'}
                                    </button>
                                  ))}
                                  <div className="min-w-[8.5rem] flex-1">
                                    <Select
                                      value={row.funktion === 'PILOT' || row.funktion === 'CO_PILOT' ? '' : row.funktion}
                                      onChange={e => setBloecke(b => b.map(bl => bl.key !== block.key ? bl : {
                                        ...bl, besatzung: bl.besatzung.map((r, j) => j === i ? { ...r, funktion: e.target.value } : r)
                                      }))}
                                      placeholder="Andere …"
                                      options={FUNKTIONEN.filter(f => f.value !== 'PILOT' && f.value !== 'CO_PILOT').map(f => ({ value: f.value, label: f.label }))}
                                    />
                                  </div>
                                </div>
                              ) : (
                              <div className="flex min-w-0 items-center gap-1">
                                <Select
                                  value={row.funktion}
                                  onChange={e => setBloecke(b => b.map(bl => bl.key !== block.key ? bl : {
                                    ...bl, besatzung: bl.besatzung.map((r, j) => j === i ? { ...r, funktion: e.target.value } : r)
                                  }))}
                                  placeholder="— Funktion —"
                                  options={FUNKTIONEN.map(f => ({ value: f.value, label: f.label }))}
                                />
                              </div>
                              )}

                              <div className={schmal ? 'grid grid-cols-2 gap-2' : 'contents'}>
                              {/* Spalte 3: Einsatzbeginn */}
                              <div className="flex min-w-0 items-center gap-1">
                                <Input type="time" className="min-w-0" value={row.beginn}
                                  onChange={e => setBloecke(b => b.map(bl => bl.key !== block.key ? bl : {
                                    ...bl, besatzung: bl.besatzung.map((r, j) => j === i ? { ...r, beginn: e.target.value } : r)
                                  }))} />
                                <button type="button" title="Jetzt setzen"
                                  onClick={() => setBloecke(b => b.map(bl => bl.key !== block.key ? bl : {
                                    ...bl, besatzung: bl.besatzung.map((r, j) => j === i ? { ...r, beginn: autoBeginn() } : r)
                                  }))}
                                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-dark-text-muted hover:bg-dark-bg-tertiary">
                                  <Clock className="h-4 w-4" />
                                </button>
                              </div>

                              {/* Spalte 4: Einsatzende */}
                              <div className="flex min-w-0 items-center gap-1">
                                <Input type="time" className="min-w-0" value={row.ende}
                                  onChange={e => setBloecke(b => b.map(bl => bl.key !== block.key ? bl : {
                                    ...bl, besatzung: bl.besatzung.map((r, j) => j === i ? { ...r, ende: e.target.value } : r)
                                  }))} />
                                <button type="button" title="Jetzt setzen"
                                  onClick={() => setBloecke(b => b.map(bl => bl.key !== block.key ? bl : {
                                    ...bl, besatzung: bl.besatzung.map((r, j) => j === i ? { ...r, ende: autoEnde() } : r)
                                  }))}
                                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-dark-text-muted hover:bg-dark-bg-tertiary">
                                  <Clock className="h-4 w-4" />
                                </button>
                              </div>
                              </div>
                            </div>
                          )
                        })}
                      </div></div>
                    )}

                    {!block.fahrzeugId && !zugeklappt[klappeKey(block)] && (
                      <p className="px-4 pb-3 text-xs text-dark-text-subtle">
                        Fahrzeug wählen – danach erscheinen die Besatzungszeilen.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            {/* ============ Reserve Bereithalteort / Wache ============ */}
            <Card className="p-4">
              <h3 className="text-base font-semibold text-dark-text">Reserve Bereithalteort / Wache</h3>
              <div
                data-drop="reserve"
                className={`mt-3 min-h-[92px] rounded-2xl border-2 border-dashed p-3 transition-colors
                  ${drag?.over === 'reserve' ? 'border-primary-500 bg-primary-600/15' : 'border-dark-border bg-dark-bg'}`}
              >
                {reserveWache.length === 0 ? (
                  <p className="flex h-[68px] items-center justify-center text-xs text-dark-text-subtle">
                    Kräfte hierher ziehen, um sie als Reserve bereitzuhalten.
                  </p>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                    {reserveWache.map(id => {
                      const k = kraftById.get(id)
                      if (!k) return null
                      return (
                        <KraftKarte key={id} kraft={k} klein onDragStart={starteDrag}
                          onEntfernen={() => entferneUeberall(id)} platzhalterText="Aus Reserve entfernen"
                          truppBadge={(() => { const t = truppVonKraft.get(id); return t ? `${t.name}${t.fuehrerId === id ? ' · TF' : ''}${t.reserve ? ' · Reserve' : ''}` : undefined })()} />
                      )
                    })}
                  </div>
                )}
              </div>
            </Card>

            {/* ============ Einsatzreserve ============ */}
            <Card className="p-4">
              <h3 className="text-base font-semibold text-dark-text">Einsatzreserve</h3>
              <div className="mt-3 overflow-hidden rounded-2xl border border-dark-border">
                <div className="grid grid-cols-[minmax(190px,1.5fr)_160px] gap-2 bg-dark-bg-tertiary px-3 py-2 text-xs font-semibold uppercase tracking-wider text-dark-text-subtle">
                  <span>Einsatzkraft</span><span>Verfügbar ab</span>
                </div>

                {einsatzReserve.map((eintrag, i) => {
                  const k = kraftById.get(eintrag.nutzerId)
                  if (!k) return null
                  return (
                    <div key={eintrag.nutzerId} className="grid grid-cols-[minmax(190px,1.5fr)_160px] items-center gap-2 border-t border-dark-border px-3 py-2">
                      <KraftKarte kraft={k} klein onDragStart={starteDrag}
                        onEntfernen={() => entferneUeberall(k.id)} platzhalterText="Aus Einsatzreserve entfernen" />
                      <div className="flex items-center gap-1">
                        <Input type="time" value={eintrag.verfuegbarAb}
                          onChange={e => setEinsatzReserve(r => r.map((x, j) => j === i ? { ...x, verfuegbarAb: e.target.value } : x))} />
                        <Clock className="h-4 w-4 shrink-0 text-dark-text-subtle" />
                      </div>
                    </div>
                  )
                })}

                {/* Drop-Zeile zum Hinzufügen */}
                <div
                  data-drop="einsatzreserve"
                  className={`border-t border-dark-border px-3 py-3 transition-colors
                    ${drag?.over === 'einsatzreserve' ? 'bg-primary-600/15' : 'bg-dark-bg'}`}
                >
                  <div className={`flex h-12 items-center justify-center gap-2 rounded-xl border border-dashed text-xs
                    ${drag?.over === 'einsatzreserve' ? 'border-primary-500 text-primary-200' : 'border-dark-border text-dark-text-subtle'}`}>
                    <ListChecks className="h-4 w-4" />
                    {drag?.over === 'einsatzreserve' ? 'Hier ablegen' : 'Kraft hierher ziehen für die Einsatzreserve'}
                  </div>
                </div>
              </div>
            </Card>

            {/* ============ Einsatzende ============ */}
            <Card className="p-4">
              <h3 className="text-base font-semibold text-dark-text">Einsatzende</h3>
              <div className="mt-3 overflow-hidden rounded-2xl border border-dark-border">
                <div className="grid grid-cols-[minmax(190px,1.5fr)_160px_160px] gap-2 bg-dark-bg-tertiary px-3 py-2 text-xs font-semibold uppercase tracking-wider text-dark-text-subtle">
                  <span>Einsatzkraft</span><span>Beginn</span><span>Ende</span>
                </div>

                {einsatzEnde.map((eintrag, i) => {
                  const k = kraftById.get(eintrag.nutzerId)
                  if (!k) return null
                  const istBeendet = !!eintrag.ende
                  return (
                    <div key={eintrag.nutzerId} className={`grid grid-cols-[minmax(190px,1.5fr)_160px_160px] items-center gap-2 border-t border-dark-border px-3 py-2 ${istBeendet ? 'opacity-60 bg-gray-900/30' : ''}`}>
                      <KraftKarte kraft={k} klein onDragStart={starteDrag}
                        onEntfernen={() => {
                          setEinsatzEnde(e => e.filter(x => x.nutzerId !== k.id))
                        }} platzhalterText="Aus Einsatzende entfernen" />
                      <div className="flex items-center gap-1">
                        <Input type="time" value={eintrag.beginn || ''}
                          onChange={e => setEinsatzEnde(r => r.map((x, j) => j === i ? { ...x, beginn: e.target.value } : x))}
                          disabled={istBeendet} />
                        <Clock className="h-4 w-4 shrink-0 text-dark-text-subtle" />
                      </div>
                      <div className="flex items-center gap-1">
                        <Input type="time" value={eintrag.ende || ''}
                          onChange={e => setEinsatzEnde(r => r.map((x, j) => j === i ? { ...x, ende: e.target.value } : x))}
                          disabled={istBeendet} />
                        {!istBeendet && (
                          <button type="button" title="Jetzt setzen"
                            onClick={() => setEinsatzEnde(r => r.map((x, j) => j === i ? { ...x, ende: autoEnde() } : x))}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-dark-text-muted hover:bg-dark-bg-tertiary">
                            <Clock className="h-4 w-4" />
                          </button>
                        )}
                        {istBeendet && (
                          <span className="text-xs text-amber-300 font-medium">beendet</span>
                        )}
                      </div>
                    </div>
                  )
                })}

                {/* Drop-Zeile zum Hinzufügen */}
                <div
                  data-drop="einsatzende"
                  className={`border-t border-dark-border px-3 py-3 transition-colors
                    ${drag?.over === 'einsatzende' ? 'bg-primary-600/15' : 'bg-dark-bg'}`}
                >
                  <div className={`flex h-12 items-center justify-center gap-2 rounded-xl border border-dashed text-xs
                    ${drag?.over === 'einsatzende' ? 'border-primary-500 text-primary-200' : 'border-dark-border text-dark-text-subtle'}`}>
                    <UserMinus className="h-4 w-4" />
                    {drag?.over === 'einsatzende' ? 'Hier ablegen' : 'Kraft hierher ziehen für das Einsatzende'}
                  </div>
                </div>
              </div>
            </Card>

          </div>
        </div>
        )}
        </div>
      )}

      {/* Drag-Ghost folgt dem Finger */}
      {drag && dragKraft && (
        <div
          className="pointer-events-none fixed z-50 w-56 rotate-2 opacity-95 shadow-2xl"
          style={{ left: drag.x - 100, top: drag.y - 28 }}
        >
          <div className="rounded-xl border border-primary-500 bg-dark-bg-tertiary px-3 py-2 shadow-2xl">
            <div className="truncate text-sm font-medium text-dark-text">
              {`${dragKraft.vorname ?? ''} ${dragKraft.nachname ?? ''}`.trim() || dragKraft.benutzername}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5">
              {dragKraft.lizenzen.map(l => (
                <span key={l.id} className="inline-flex items-center gap-0.5">
                  <LizenzIcon typ={l.typ} />
                  <span className="text-xs text-dark-text-subtle">{kurzName(l.typ ?? '')}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
